/**
 * GCP deep dive — both billing accounts (*Brkaway – Winclap* and *Tech – Winclap*),
 * queried live off the billing export tables rather than from a snapshot table.
 *
 * Two things differ from the AWS tab beyond the vocabulary (project, not account):
 * the projection is a run-rate estimate with no prediction interval — it must never
 * read as authoritative as Cost Explorer's forecast — and the export only started
 * accumulating in early August 2026, so any earlier period legitimately returns zero
 * rows. That is an empty state, not an error.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Cloud, Crown, TrendingUp } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  useFinopsGcpForecast,
  useFinopsGcpMonth,
  useFinopsGcpResources,
  useFinopsGcpWeekly,
  useFinopsHistory,
} from '@/hooks/useFinops';
import { AccountOrProjectTable, type EntityRow } from './AccountOrProjectTable';
import { ForecastCard } from './ForecastCard';
import { HistoryStrip, type HistoryGranularity } from './HistoryStrip';
import { ResourceDrawer } from './ResourceDrawer';
import { ServiceTable } from './ServiceTable';
import { WeeklyDigestPanel } from './WeeklyDigestPanel';
import {
  EmptyState, LoaderBlock, StatCard, addDaysISO, deltaOf, fmtDelta, fmtMonth, fmtUSD, fmtWeekRange,
} from './shared';

const ALL_PROJECTS = '__all__';
/** 3 months ≈ the 90-day default; each "Load more" pages ~60 days further back. */
const HISTORY_START_MONTHS = 3;
const HISTORY_STEP_MONTHS = 2;
const HISTORY_MAX_MONTHS = 36;

/**
 * Some services (Networking, Artifact Registry) bill against no resource and come
 * back with an empty string rather than NULL, so the helper's COALESCE misses them.
 * Normalising here keeps them visually identical to the NULL case the helper labels.
 */
const NO_RESOURCE = '(no resource)';

export function FinopsGcpTab() {
  const { t } = useTranslation();

  const [project, setProject] = useState<string>(ALL_PROJECTS);
  const [drawerService, setDrawerService] = useState<string | undefined>();
  const [historyMonths, setHistoryMonths] = useState(HISTORY_START_MONTHS);
  const [granularity, setGranularity] = useState<HistoryGranularity>('month');

  const { data: month, isLoading: monthLoading } = useFinopsGcpMonth();
  const { data: week, isLoading: weekLoading } = useFinopsGcpWeekly();
  // Per-service WoW deltas need the previous week, which only exists once the
  // current week's period is known — hence the deferred second query.
  const priorWeekStart = week ? addDaysISO(week.period, -7) : undefined;
  const { data: priorWeek } = useFinopsGcpWeekly(priorWeekStart, !!priorWeekStart);
  const { data: forecast } = useFinopsGcpForecast();
  const { data: history, isFetching: historyFetching } = useFinopsHistory('gcp', historyMonths);

  const drawerOpen = !!drawerService;
  const { data: rawResources = [], isLoading: resourcesLoading } = useFinopsGcpResources(
    week?.period ?? '',
    project === ALL_PROJECTS ? undefined : project,
    drawerService,
    drawerOpen && !!week?.period,
  );

  // Rows keep the backend's order (featured first, then cost) — the table renders
  // one header per billing account off `group`.
  const projectRows: EntityRow[] = useMemo(
    () => (month?.projects ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      cost: p.cost,
      group: t(p.billingGroup === 'brkaway' ? 'finops.gcp.group_brkaway' : 'finops.gcp.group_tech'),
      featured: p.featured,
    })),
    [month, t],
  );

  // Project filter is a client-side fold of the matrix that already travelled.
  const services = useMemo(() => {
    if (!month) return [];
    if (project === ALL_PROJECTS) return month.services;
    const byService = new Map<string, number>();
    for (const row of month.matrix) {
      if (row.accountId !== project) continue;
      byService.set(row.service, (byService.get(row.service) ?? 0) + row.cost);
    }
    return Array.from(byService.entries())
      .map(([service, cost]) => ({ service, cost }))
      .sort((a, b) => b.cost - a.cost);
  }, [month, project]);

  const resources = useMemo(
    () => rawResources.map((r) => ({ ...r, resourceId: r.resourceId || NO_RESOURCE })),
    [rawResources],
  );

  if (monthLoading && weekLoading) return <LoaderBlock />;

  const wow = week ? deltaOf(week.total, week.priorTotal) : null;
  const projectName = projectRows.find((p) => p.id === project)?.name;
  const canLoadMore = historyMonths < HISTORY_MAX_MONTHS;

  return (
    <div className="space-y-4">
      {/* 1 — Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={CalendarRange}
          tone="blue"
          label={t('finops.gcp.stat_prev_month')}
          value={fmtUSD(month?.total)}
          sub={month?.period ? fmtMonth(month.period) : undefined}
        />
        <StatCard
          icon={Cloud}
          tone="purple"
          label={t('finops.gcp.stat_week')}
          value={fmtUSD(week?.total)}
          sub={week ? `${fmtWeekRange(week.period)} · ${fmtDelta(wow)} ${t('finops.cloud.digest_wow')}` : undefined}
        />
        <StatCard
          icon={TrendingUp}
          tone={
            forecast && forecast.priorMonthActual > 0 && forecast.projected >= forecast.priorMonthActual
              ? 'rose'
              : 'green'
          }
          label={t('finops.gcp.stat_projection')}
          value={fmtUSD(forecast?.projected)}
          sub={
            forecast
              ? t('finops.gcp.stat_projection_sub', {
                  delta: fmtDelta(deltaOf(forecast.projected, forecast.priorMonthActual)),
                })
              : undefined
          }
        />
        <StatCard
          icon={Crown}
          tone="amber"
          label={t('finops.gcp.stat_top_service')}
          value={month?.services[0]?.service ?? '—'}
          sub={month?.services[0] ? fmtUSD(month.services[0].cost) : undefined}
        />
      </div>

      {/* 2 — Weekly digest (hero) + run-rate projection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <WeeklyDigestPanel
            week={week}
            priorWeek={priorWeek}
            emptyDescription={t('finops.gcp.week_empty_description')}
          />
        </div>
        {/* No `interval` and no `snapshotDate`: this is a live run-rate, not a
            Cost Explorer forecast, and the card must not imply otherwise. */}
        <ForecastCard
          period={forecast?.period}
          projected={forecast?.projected}
          mtdActual={forecast?.mtdActual}
          priorMonthActual={forecast?.priorMonthActual}
          badge={t('finops.gcp.forecast_badge')}
          note={
            forecast
              ? t('finops.gcp.forecast_note', {
                  avg: fmtUSD(forecast.dailyAvg7d),
                  elapsed: forecast.daysElapsed,
                  total: forecast.daysInMonth,
                })
              : undefined
          }
          emptyDescription={t('finops.gcp.forecast_empty_description')}
        />
      </div>

      {/* 3 — Previous-month detail */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <h2 className="text-sm font-semibold">
          {t('finops.gcp.detail_title')}
          {month?.period && (
            <span className="text-muted-foreground font-normal"> · {fmtMonth(month.period)}</span>
          )}
        </h2>
        <Select value={project} onValueChange={setProject}>
          <SelectTrigger className="h-8 text-sm w-64">
            <SelectValue placeholder={t('finops.gcp.project_filter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_PROJECTS}>{t('finops.gcp.all_projects')}</SelectItem>
            {projectRows.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!month || month.total === 0 ? (
        <EmptyState
          title={t('finops.cloud.month_empty_title')}
          description={t('finops.gcp.month_empty_description')}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AccountOrProjectTable
            title={t('finops.gcp.projects_title')}
            rows={projectRows}
            total={month.total}
            selectedId={project === ALL_PROJECTS ? undefined : project}
            onSelect={(id) => setProject(id ?? ALL_PROJECTS)}
            entityColLabel={t('finops.gcp.col_project')}
            emptyDescription={t('finops.gcp.month_empty_description')}
          />
          <ServiceTable
            title={t('finops.cloud.services_title')}
            subtitle={project === ALL_PROJECTS ? undefined : projectName ?? project}
            rows={services}
            onOpenResources={week?.period ? setDrawerService : undefined}
          />
        </div>
      )}

      {/* 4 — History */}
      <HistoryStrip
        data={history}
        granularity={granularity}
        onGranularityChange={setGranularity}
        onLoadMore={() => setHistoryMonths((m) => Math.min(HISTORY_MAX_MONTHS, m + HISTORY_STEP_MONTHS))}
        canLoadMore={canLoadMore}
        isLoading={historyFetching}
        emptyDescription={t('finops.gcp.history_empty_description')}
      />

      {/* 5 — Footnote */}
      <p className="text-xs text-muted-foreground">{t('finops.cloud.footnote')}</p>

      <ResourceDrawer
        open={drawerOpen}
        onOpenChange={(open) => !open && setDrawerService(undefined)}
        service={drawerService}
        periodLabel={week?.period ? fmtWeekRange(week.period) : undefined}
        rows={resources}
        isLoading={resourcesLoading}
        entityColLabel={t('finops.gcp.col_project')}
        emptyDescription={t('finops.gcp.resources_empty_description')}
        filterLabel={project === ALL_PROJECTS ? undefined : projectName ?? project}
      />
    </div>
  );
}
